import axios from 'axios';
import * as Sentry from '@sentry/react';
import store from '../store';
import { updateAccessToken } from '../features/auth/authSlice';

const FAIRDAY_API_URL = import.meta.env.VITE_APP_API_BASE_URL || 'http://localhost:3001';

export const ApiInstance = axios.create({
  baseURL: FAIRDAY_API_URL.endsWith('/') ? FAIRDAY_API_URL.slice(0, -1) : FAIRDAY_API_URL,
  withCredentials: true, // Send cookies with every request
  timeout: 30000, // 30s — prevents requests from hanging indefinitely
});

ApiInstance.interceptors.request.use(
  (config) => {
    // Prefix /api to the URL if it's an absolute path relative to host
    if (config.url && config.url.startsWith('/') && !config.url.startsWith('/api/')) {
      config.url = `/api${config.url}`;
    } else if (config.url && !config.url.startsWith('http') && !config.url.startsWith('api/') && !config.url.startsWith('/api/')) {
      config.url = `api/${config.url}`;
    }

    const state = store.getState();
    const accessToken = state.auth.accessToken;
    if (accessToken && accessToken !== 'null') {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    if (import.meta.env.DEV) {
      console.error('Request interceptor error');
    }
    return Promise.reject(error);
  },
);

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let hasRedirectedToLogin = false;

// Redirect to login page when session expires (deduplicated)
const redirectToLogin = () => {
  if (hasRedirectedToLogin) return;
  if (!window.location.pathname.includes('/signin')) {
    hasRedirectedToLogin = true;
    sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
    window.location.href = '/signin?session_expired=true';
  }
};

ApiInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response) {
      // Network error / timeout — report to Sentry
      Sentry.captureException(error, { tags: { source: 'axios', type: 'network' } });
      return Promise.reject(error);
    }

    const status = error.response.status;
    const originalRequest = error.config;

    // Report server errors (5xx) to Sentry
    if (status >= 500) {
      Sentry.captureException(error, {
        tags: { source: 'axios', statusCode: String(status) },
        extra: { url: originalRequest?.url, method: originalRequest?.method },
      });
    }

    // Only attempt refresh on 401 (expired access token)
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Deduplicate concurrent refresh calls
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = ApiInstance.post('/advertisers/auth/refresh-token')
          .then((res) => {
            const { accessToken } = res.data;
            store.dispatch(updateAccessToken(accessToken));
            return accessToken;
          })
          .catch((refreshError) => {
            store.dispatch(updateAccessToken(null));
            // Only redirect on auth failures (401/403), not network errors
            const refreshStatus = refreshError?.response?.status;
            if (refreshStatus === 401 || refreshStatus === 403) {
              redirectToLogin();
            }
            throw refreshError;
          })
          .finally(() => {
            isRefreshing = false;
            refreshPromise = null;
          });
      }

      try {
        const newAccessToken = await refreshPromise!;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return ApiInstance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);
